import React from "react";
import { Box, Button, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { LockedPayoutAccount } from "../../common/requests/transactions/endpoints";
import { C } from "../../common/theme";
import { formatAge } from "../../common/utils/format";
import { CopyValue, DetailList } from "../../ui";
import { usePayoutKey } from "./PayoutKeyProvider";
import { accountTitle, countryName, recentlyChanged } from "./payoutLabels";

/** Where to send a seller's money: bank, holder, SWIFT and the number
 * (masked until unlocked). Warns when the details changed in the last week. */
export default function PayToDetails({ account, plain }: { account: LockedPayoutAccount; plain?: string }) {
  const { key, requestUnlock } = usePayoutKey();
  const change = account.last_change;
  const recent = recentlyChanged(account);
  const numberLabel = account.method === "paynow" ? (account.paynow_type === "uen" ? "PayNow UEN" : "PayNow mobile") : "Account number";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      {recent && change && (
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", p: 1.25, borderRadius: "8px", bgcolor: C.bone1, border: "1px solid #F2C5AF", fontSize: 13 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 18, color: C.coralText, mt: "1px" }} />
          <span>
            <b>Changed {change.changed_at ? formatAge(change.changed_at) : "recently"}</b>
            {change.old_last4 ? ` (was •••• ${change.old_last4})` : " (first time)"}. Check with the seller before a large payout.
          </span>
        </Box>
      )}
      <DetailList
        items={[
          { label: account.method === "paynow" ? "Pay by" : "Bank", value: accountTitle(account) },
          { label: "Account holder", value: account.account_holder_name },
          {
            label: numberLabel,
            full: true,
            value: plain ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <LockOpenOutlinedIcon sx={{ fontSize: 16, color: C.leaf }} />
                <CopyValue value={plain} mono />
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Box component="span" className="mono" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: C.muted }}>
                  <LockOutlinedIcon sx={{ fontSize: 16 }} />•••• {account.last4}
                </Box>
                {!key && (
                  <Button size="small" variant="outlined" onClick={() => requestUnlock(account.ciphertext)} sx={{ minHeight: 34 }}>
                    Unlock
                  </Button>
                )}
                {key && (
                  <Typography variant="caption" sx={{ color: C.danger }}>
                    Your key doesn't open this one.
                  </Typography>
                )}
              </Box>
            ),
          },
          ...(account.bank_code ? [{ label: "SWIFT code", value: <CopyValue value={account.bank_code} mono /> }] : []),
          { label: "Country", value: countryName(account.country) },
          { label: "Saved", value: account.updated_at ? formatAge(account.updated_at) : "–" },
        ]}
      />
    </Box>
  );
}
